import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Info } from '@ui/components/icons';
import { TextTooltip } from '@ui/components/TextTooltip';
import {
  calculateSpreadPercentage,
  useSpreadPercentage,
} from '@ui/features/Spread/useSpreadPercentage';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { Trans, useTranslation } from 'next-i18next';
import { css } from 'styled-system/css';
import { Box, HStack } from 'styled-system/jsx';
import { SystemStyleObject } from 'styled-system/types';

import { formatSlippage } from '../../utils/formatSlippage';

export const MarketPrice = ({
  position,
  valueStyle,
}: {
  position: Position;
  valueStyle: SystemStyleObject;
}) => {
  const { t } = useTranslation();
  const marketPrice = usePriceLive(position.pair.baseSymbol);
  const priceDisplayDecimals = useTokenDisplayDecimals(
    position.pair.baseSymbol,
    position.pair.priceDisplayDecimals,
  );

  const pairInfo = useTradePairInfosByPairBase(position.pair.address);

  const spreadPercentageInfo = useSpreadPercentage({
    tradePair: pairInfo,
    positionSize: position.getSizeUsd(marketPrice ?? ZERO_BIG_NUMBER),
    pairPrice: marketPrice,
  });

  return (
    <Box>
      <AmountUsd
        css={valueStyle}
        value={marketPrice ?? 0}
        opts={{
          displayDecimals: priceDisplayDecimals,
        }}
        data-cy="MarketPrice"
        data-cy-value={marketPrice ?? 0}
      />
      <TextTooltip
        content={
          <Trans
            i18nKey="common.slippage-indicator-close-position"
            components={{
              T1: <Text textStyle="textParagraph.small" />,
            }}
          />
        }
        contentCss={css.raw({
          maxW: '250px',
        })}
        triggerCss={css.raw({
          textDecoration: 'none',
        })}
      >
        <HStack gap={1} mt="5px">
          <Text textStyle="text.xxsmall">
            {t('common.{slippagePercentage} spread applies', {
              slippagePercentage: marketPrice
                ? formatSlippage(
                    calculateSpreadPercentage({
                      isClosePrice: true,
                      spreadPercentageInfo,
                      isLong: position.isLong,
                    }),
                  )
                : '-%', // if market price is not ready, show '%' default
            })}
          </Text>
          <Icon boxSize="3">
            <Info />
          </Icon>
        </HStack>
      </TextTooltip>
    </Box>
  );
};
